/** Outlook calendars: any member's, plus the company calendar (`routers/calendar.py`). The page's decisions are pure helpers here because vitest cannot render a component:
 *  which local day an event falls on (timed events arrive as UTC instants; Mongolia is UTC+8, so a 20:00Z event is TOMORROW morning), how all-day events span days (end exclusive), the duration label, and the degraded-state sentence.
 *  No `api` import: `lib/api.ts` pulls in MSAL, which needs `window`, so the query hooks live in `pages/calendar/CalendarPage.tsx`. */
import { addMonths, monthGridRange } from '../pages/time/dates'

export type CalendarState =
  | 'ok'
  | 'no_mailbox'
  | 'not_offered'
  | 'not_configured'
  | 'consent_pending'
  | 'unavailable'

export interface CalendarEventApi {
  id: string
  subject: string
  location: string
  organizer: string
  body_preview: string
  is_all_day: boolean
  /** All-day: `YYYY-MM-DD`, end EXCLUSIVE. Timed: a UTC instant ending in `Z`. */
  start: string
  end: string
  show_as: string
  sensitivity: string
}

type CalendarSource = 'person' | 'company'

export interface CalendarEvent extends CalendarEventApi {
  source: CalendarSource
}

export interface CalendarEventsResponse {
  state: CalendarState
  events: CalendarEventApi[]
}

interface CalendarPerson {
  id: string
  name: string
  has_mailbox: boolean
}

export interface CalendarPeopleResponse {
  me: string
  company: { configured: boolean }
  people: CalendarPerson[]
}

const MINUTE = 60_000
const DAY = 86_400_000

/** The browser's offset from UTC in minutes, east positive (Ulaanbaatar: 480). */
export function browserOffsetMinutes(at: Date = new Date()): number {
  return -at.getTimezoneOffset()
}

/** `YYYY-MM-DD` of `ms` (epoch) at a fixed offset. */
function dateAt(ms: number, offsetMinutes: number): string {
  return new Date(ms + offsetMinutes * MINUTE).toISOString().slice(0, 10)
}

/** The local calendar date of a UTC instant at `offsetMinutes`. */
export function localDateOfInstant(instant: string, offsetMinutes: number): string {
  return dateAt(Date.parse(instant), offsetMinutes)
}

/** `HH:MM` of a UTC instant at `offsetMinutes`. */
export function localTimeOfInstant(instant: string, offsetMinutes: number): string {
  return new Date(Date.parse(instant) + offsetMinutes * MINUTE).toISOString().slice(11, 16)
}

function addDaysIso(value: string, days: number): string {
  return new Date(Date.parse(`${value}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10)
}

/** Every local date an event occupies, in order. All-day: start to end-1. Timed: start's local date to the end's, except an event ending EXACTLY at local midnight does not occupy the next day. A zero-length event occupies its start day. */
export function eventDays(event: CalendarEventApi, offsetMinutes: number): string[] {
  let first: string
  let last: string
  if (event.is_all_day) {
    first = event.start
    last = event.end > event.start ? addDaysIso(event.end, -1) : event.start
  } else {
    const startMs = Date.parse(event.start)
    const endMs = Math.max(Date.parse(event.end), startMs)
    first = dateAt(startMs, offsetMinutes)
    last = endMs > startMs ? dateAt(endMs - 1, offsetMinutes) : first
  }
  const out: string[] = []
  // Bounded: the API window is at most 62 days.
  for (let d = first; d <= last && out.length < 400; d = addDaysIso(d, 1)) out.push(d)
  return out
}

/** Events keyed by local date; all-day first, then by start, then subject. */
export function bucketByDay(
  events: CalendarEvent[],
  offsetMinutes: number,
): Map<string, CalendarEvent[]> {
  const out = new Map<string, CalendarEvent[]>()
  for (const event of events) {
    for (const day of eventDays(event, offsetMinutes)) {
      const list = out.get(day)
      if (list) list.push(event)
      else out.set(day, [event])
    }
  }
  for (const list of out.values()) {
    list.sort((a, b) => {
      if (a.is_all_day !== b.is_all_day) return a.is_all_day ? -1 : 1
      if (a.start !== b.start) return a.start < b.start ? -1 : 1
      return a.subject.localeCompare(b.subject)
    })
  }
  return out
}

/** Minutes between start and end of a timed event; null for all-day. */
export function durationMinutes(event: CalendarEventApi): number | null {
  if (event.is_all_day) return null
  return Math.max(0, Math.round((Date.parse(event.end) - Date.parse(event.start)) / MINUTE))
}

/** `1h 30m`, `45m`, `2h`, or `3d 2h` for an event over a day. */
export function formatDuration(minutes: number): string {
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  const parts: string[] = []
  if (days) parts.push(`${days}d`)
  if (hours) parts.push(`${hours}h`)
  if (mins || parts.length === 0) parts.push(`${mins}m`)
  return parts.join(' ')
}

/** The sentence a non-`ok` state shows, as an i18n key; null for `ok`. */
export function stateMessageKey(state: CalendarState | string): string | null {
  switch (state) {
    case 'ok':
      return null
    case 'no_mailbox':
      return 'calendar.stateNoMailbox'
    case 'not_offered':
      return 'calendar.stateNotOffered'
    case 'not_configured':
      return 'calendar.stateNotConfigured'
    case 'consent_pending':
      return 'calendar.stateConsentPending'
    default:
      return 'calendar.stateUnavailable'
  }
}

/** The window the month grid must load: local midnight of its first cell to local midnight after its last, as UTC instants (`Z`) so no `+08:00` survives a query string. At most 42 days, inside the server's 62. */
export function requestWindow(month: string, offsetMinutes: number): { start: string; end: string } {
  const { from, to } = monthGridRange(month)
  const midnight = (iso: string) =>
    new Date(Date.parse(`${iso}T00:00:00Z`) - offsetMinutes * MINUTE).toISOString()
  return { start: midnight(from), end: midnight(addDaysIso(to, 1)) }
}

/** The request windows of the months either side of `month`, for prefetch; the same windows `requestWindow` builds, so the query keys match exactly. */
export function adjacentWindows(
  month: string,
  offsetMinutes: number,
): { start: string; end: string }[] {
  return [addMonths(month, -1), addMonths(month, 1)].map((m) => requestWindow(m, offsetMinutes))
}

/** One list from the person's calendar and the company's. A company invitation also sits in the invitee's own calendar under a different id, so an event with the same subject, start and end is kept ONCE, as the company's. */
export function mergeCalendars(
  person: CalendarEventApi[],
  company: CalendarEventApi[],
): CalendarEvent[] {
  const key = (e: CalendarEventApi) => `${e.subject}\u0000${e.start}\u0000${e.end}`
  const companyKeys = new Set(company.map(key))
  return [
    ...company.map((e) => ({ ...e, source: 'company' as const })),
    ...person.filter((e) => !companyKeys.has(key(e))).map((e) => ({ ...e, source: 'person' as const })),
  ]
}

/** The people the picker offers: those with a mailbox, plus the viewer always. */
export function pickerPeople(people: CalendarPerson[], me: string): CalendarPerson[] {
  return people.filter((p) => p.has_mailbox || p.id === me)
}
